import { useState, useRef, useEffect } from "react";
import { encodeImageFileAsURL } from "utils/fileToBase64";
import { useFieldArray, useForm } from "react-hook-form";
import { FormCheck, ImageModal, FormEntry } from "components";
import { FiX } from "react-icons/fi";
import { getDataURIMimeType, mimeTypes } from "utils/getDataURIMimeType";
import { openBase64InNewTab } from "utils/openBase64InNewTab";

const imageTypes = {
	CLASSWORK: "classworkImages",
	IN_CLASS_IMAGES: "inClassImages",
};

const initialImageModalProps = {
	open: false,
	index: null,
	type: imageTypes.CLASSWORK,
};

export const SixthPage = ({ register, errors, watch, studentPrefix, setValue, getValues, control }) => {
	const classworkInputRef = useRef();
	const inClassInputRef = useRef();
	const { register: registerSubmitClasswork, watch: watchIsNotSubmittingClasswork } = useForm({
		defaultValues: { isNotSubmittingClasswork: false },
		reValidateMode: "onChange",
	});
	// register, name, (altStatus = false), formLabel, error, (editable = true), (margin = true);

	const {
		fields: classworkImages,
		append: appendClassworkImage,
		remove: removeClassworkImage,
	} = useFieldArray({
		control,
		name: `studentReport.${studentPrefix}.${imageTypes.CLASSWORK}`,
	});

	const {
		fields: inClassImages,
		append: appendInClassImage,
		remove: removeInClassImage,
	} = useFieldArray({
		control,
		name: `studentReport.${studentPrefix}.${imageTypes.IN_CLASS_IMAGES}`,
	});

	const [imageModalProps, setImageModalProps] = useState(initialImageModalProps);

	const appendFileToUploadedImg = async (e) => {
		if (!e.target.files.length) return;
		await appendMultipleFiles(e.target.name, e.target.files);
		e.target.value = "";
	};

	const appendMultipleFiles = async (imageType, files) => {
		const appendFn = imageType === imageTypes.CLASSWORK ? appendClassworkImage : appendInClassImage;
		for (var i = 0; i < files.length; i++) {
			const imgURI = await encodeImageFileAsURL(files[i]);
			appendFn({ data: imgURI });
		}
	};

	const removeFileFromUploadedImg = async (index, type) => {
		if (type === imageTypes.CLASSWORK) return removeClassworkImage(index);

		if (type === imageTypes.IN_CLASS_IMAGES) return removeInClassImage(index);
	};

	const isNotSubmittingClasswork = watchIsNotSubmittingClasswork("isNotSubmittingClasswork");

	useEffect(() => {
		if (isNotSubmittingClasswork) {
			setValue(`studentReport.${studentPrefix}.${imageTypes.CLASSWORK}`, undefined);
			setValue(`studentReport.${studentPrefix}.${imageTypes.IN_CLASS_IMAGES}`, undefined);
		}
	}, [isNotSubmittingClasswork, studentPrefix]);

	useEffect(() => {
		if (isNotSubmittingClasswork) {
			register(`studentReport.${studentPrefix}.nonSubmitClassworkJustification`, {
				required: isNotSubmittingClasswork
					? {
							value: true,
							message: `Justification is required`,
					  }
					: { value: false },
			});
			register(`studentReport.${studentPrefix}.${imageTypes.CLASSWORK}`, {
				required: !isNotSubmittingClasswork
					? {
							value: true,
							message: `Image upload is required`,
					  }
					: { value: false },
			});
			register(`studentReport.${studentPrefix}.${imageTypes.IN_CLASS_IMAGES}`, {
				required: !isNotSubmittingClasswork
					? {
							value: true,
							message: `Image upload is required`,
					  }
					: { value: false },
			});
		}
	}, [isNotSubmittingClasswork]);

	return (
		<>
			<FormCheck register={registerSubmitClasswork} name="isNotSubmittingClasswork" formLabel="Not Submitting Classwork" />

			<ImageModal open={imageModalProps.open} hideModal={() => setImageModalProps(initialImageModalProps)}>
				<img
					src={
						imageModalProps.type === imageTypes.CLASSWORK
							? classworkImages[imageModalProps.index]?.data || ""
							: inClassImages[imageModalProps.index]?.data || ""
					}
					alt=""
				/>
			</ImageModal>

			{!isNotSubmittingClasswork ? (
				<>
					<div className="px-3 py-2 pb-4 rounded-lg" style={{ border: "1px solid #ADB5DB" }}>
						<h5 className=" font-weight-bold text-white mb-1">
							Classwork <small className="text-info ml-2">*Optional</small>
						</h5>
						<input
							type="file"
							multiple
							className="form-control d-none"
							onChange={appendFileToUploadedImg}
							name={imageTypes.CLASSWORK}
							accept="application/pdf, image/*"
							ref={classworkInputRef}
						/>
						{errors.studentReport?.[studentPrefix]?.[imageTypes.CLASSWORK] && (
							<small className="text-danger">{errors.studentReport?.[studentPrefix]?.[imageTypes.CLASSWORK]?.message}</small>
						)}
						<div className="mt-3">
							<small
								className="border border-light rounded px-3 py-2 text-light"
								style={{ cursor: "pointer" }}
								onClick={() => {
									classworkInputRef.current?.click();
								}}
							>
								Click to upload
							</small>
						</div>

						{Boolean(classworkImages.length) && (
							<div className="row mt-3 ">
								{classworkImages.map((resource, index) => (
									<div className="col-4" key={resource.id}>
										<span
											className="border border-danger"
											style={{ cursor: "pointer", borderRadius: 5 }}
											onClick={() => removeFileFromUploadedImg(index, imageTypes.CLASSWORK)}
										>
											<FiX className="text-danger" />
											<small className="text-danger mx-1">Remove</small>
										</span>
										<div className="mt-1">
											<small>
												{getDataURIMimeType(resource.data) !== mimeTypes.pdf ? (
													<img
														src={resource.data}
														className="img-fluid rounded text-white"
														alt="Resource Uploaded"
														onClick={() =>
															setImageModalProps({
																open: true,
																index,
																type: imageTypes.CLASSWORK,
															})
														}
													/>
												) : (
													<span
														onClick={() => openBase64InNewTab(resource.data)}
														style={{ cursor: "pointer", borderRadius: 5, padding: 2 }}
														className="border border-success text-success px-1"
													>
														View Resource {index + 1}
													</span>
												)}
											</small>
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					<div className="px-3 py-2 pb-4 rounded-lg mt-5" style={{ border: "1px solid #ADB5DB" }}>
						<h5 className=" font-weight-bold text-white mb-1">
							In Class Images <small className="text-info ml-2">*Optional</small>
						</h5>

						<input
							type="file"
							multiple
							className="form-control d-none"
							onChange={appendFileToUploadedImg}
							accept="application/pdf, image/*"
							name={imageTypes.IN_CLASS_IMAGES}
							ref={inClassInputRef}
						/>
						{errors.studentReport?.[studentPrefix]?.[imageTypes.IN_CLASS_IMAGES] && (
							<small className="text-danger">{errors.studentReport?.[studentPrefix]?.[imageTypes.IN_CLASS_IMAGES]?.message}</small>
						)}
						<div className="mt-3">
							<small
								className="border border-light rounded px-3 py-2 text-light"
								style={{ cursor: "pointer" }}
								onClick={() => inClassInputRef.current?.click()}
							>
								Click to upload
							</small>
						</div>

						{Boolean(inClassImages.length) && (
							<div className="row mt-3">
								{inClassImages.map((resource, index) => (
									<div className="col-4" key={resource.id}>
										<span
											className="border border-danger"
											style={{ cursor: "pointer", borderRadius: 5 }}
											onClick={() => removeFileFromUploadedImg(index, imageTypes.IN_CLASS_IMAGES)}
										>
											<FiX className="text-danger" />
											<small className="text-danger mx-1">Remove</small>
										</span>
										<div className="mt-1">
											<small>
												{getDataURIMimeType(resource.data) !== mimeTypes.pdf ? (
													<img
														src={resource.data}
														className="img-fluid rounded text-white"
														alt="Resource Uploaded"
														onClick={() =>
															setImageModalProps({
																open: true,
																index,
																type: imageTypes.IN_CLASS_IMAGES,
															})
														}
													/>
												) : (
													<span
														onClick={() => openBase64InNewTab(resource.data)}
														style={{ cursor: "pointer", borderRadius: 5, padding: 2 }}
														className="border border-success text-success px-1"
													>
														View Resource {index + 1}
													</span>
												)}
											</small>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</>
			) : (
				<FormEntry
					formLabel="Justification"
					type="text"
					name={`studentReport.${studentPrefix}.nonSubmitClassworkJustification`}
					register={register}
					error={errors.studentReport?.[studentPrefix]?.nonSubmitClassworkJustification}
					required={true}
					placeholder="Justification for not submitting classwork"
				/>
			)}
		</>
	);
};
